import { ConnectedRouter } from 'connected-next-router';
import { memo, useMemo } from 'react';

import { ModalProvider } from '@air/provider-modal';
import { ShortIdProvider } from '@air/provider-short-id';
import { ErrorBoundary } from '@air/utils-error';

import { AccountProvisionerProvider } from '~/components/AccountProvisioner/providers/AccountProvisionerProvider';
import { PublicAssetSEO } from '~/components/Layouts/PublicBoardLayout/PublicAssetSEO';
import { MaintenanceMode } from '~/components/MaintenanceMode/MaintenanceMode';
import { PublicAsset } from '~/components/PublicAssetModal/PublicAsset';
import { PublicAssetReduxProvider } from '~/components/PublicAssetReduxProvider';
import { PublicClipActivityContainer } from '~/components/PublicClipActivityContainer';
import { PublicClipSocketContextProvider } from '~/components/PublicClipSocketContextProvider';
import { PublicPermissionsProvider } from '~/components/PublicPermissionsProvider';
import { PublicShareWorkspaceAccessProvider } from '~/components/PublicShareWorkspaceAccessProvider';
import { PublicWorkspaceProvider } from '~/components/PublicWorkspaceProvider';
import { PublicAccountProvider } from '~/providers/AccountProvider';
import { AnnotationProvider } from '~/providers/AnnotationProvider/AnnotationProvider';
import { ExternalLinksClickableProvider } from '~/providers/ExternalLinksClickableProvider';
import { FiltersProvider } from '~/providers/FiltersProvider';
import { PublicClipCustomFieldsProvider } from '~/providers/PublicClipCustomFieldsProvider';
import { PublicUrlAssetModalNavigationProvider } from '~/providers/PublicUrlAssetModalNavigationProvider';
import { ReCaptchaProvider } from '~/providers/ReCaptchaProvider';
import { type PublicClipShareData } from '~/swr-hooks/shortUrl/utils';

export type PublicClipProps = {
  initialData: PublicClipShareData;
  shortId: string;
};

export const PublicClip = memo(({ shortId, initialData }: PublicClipProps) => {
  const workspace = useMemo(
    () => ({
      id: initialData.clip.workspaceId,
      name: initialData.clip.workspaceName,
    }),
    [initialData.clip],
  );

  return (
    <ReCaptchaProvider siteKey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}>
      <AccountProvisionerProvider>
        <PublicAccountProvider workspace={workspace}>
          <PublicPermissionsProvider permissions={initialData.permissions}>
            <PublicAssetReduxProvider initialClip={initialData.clip}>
              <ExternalLinksClickableProvider areExternalLinksClickable={initialData.isOnPaidPlan === true}>
                <ShortIdProvider
                  shortId={shortId}
                  objectId={initialData.objectId}
                  objectType={initialData.objectType}
                  presentationSettings={initialData.presentationSettings}
                  isOnPaidPlan={initialData.isOnPaidPlan}
                >
                  <PublicUrlAssetModalNavigationProvider>
                    <PublicWorkspaceProvider {...initialData.clip}>
                      <PublicShareWorkspaceAccessProvider>
                        <ErrorBoundary>
                          <PublicClipSocketContextProvider>
                            <ConnectedRouter>
                              <FiltersProvider>
                                <ModalProvider>
                                  <MaintenanceMode>
                                    <PublicClipCustomFieldsProvider>
                                      <PublicAssetSEO clip={initialData.clip} />
                                      <AnnotationProvider>
                                        <PublicAsset
                                          requireAccount={initialData.requireAccount}
                                          initialClip={initialData.clip}
                                        />
                                      </AnnotationProvider>
                                      <PublicClipActivityContainer />
                                    </PublicClipCustomFieldsProvider>
                                  </MaintenanceMode>
                                </ModalProvider>
                              </FiltersProvider>
                            </ConnectedRouter>
                          </PublicClipSocketContextProvider>
                        </ErrorBoundary>
                      </PublicShareWorkspaceAccessProvider>
                    </PublicWorkspaceProvider>
                  </PublicUrlAssetModalNavigationProvider>
                </ShortIdProvider>
              </ExternalLinksClickableProvider>
            </PublicAssetReduxProvider>
          </PublicPermissionsProvider>
        </PublicAccountProvider>
      </AccountProvisionerProvider>
    </ReCaptchaProvider>
  );
});

PublicClip.displayName = 'PublicClip';
